import React, { useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import {
  Archive,
  ArchiveRestore,
  ArrowRight,
  Clock3,
  Layers3,
  Pencil,
  Grid2X2,
  Play,
  Route,
  Trash2,
} from "lucide-react";
import type { Recording, RecordingSyncStatus, StatusPayload } from "../../api/types";
import { formatDuration, parseUtcDate } from "../../utils";
import { usePageAsk } from "../../chat/AskContext";
import { pageDateFilters } from "../../chat/askScope";
import DashboardDateRange from "./DashboardDateRange";
import { useActivityDates } from "./useActivityDates";
import ActivityMetric from "./ActivityMetric";
import { activitySeconds } from "./activityModel";
import { useProcessOverview } from "./useProcessOverview";
import RecordingSyncRow from "./RecordingSyncRow";
import PendingReviewSection from "./PendingReviewSection";
import SessionRenameDialog from "./SessionRenameDialog";
import MySessionRow, { type SessionAction } from "./MySessionRow";
import { canAnalyzeSession } from "./SessionStatus";
import SessionFilterControls from "./SessionFilterControls";
import { matchesSessionFilters } from "./sessionFilterModel";
import { useSessionFilters } from "./useActivityFilters";
import RecordingBulkActionDialog from "../RecordingBulkActionDialog";
import { useActionItems } from "./ActionItemsContext";

export default function MyActivity({
  peopleControl,
  recordings,
  userId,
  loading,
  error,
  updateError,
  refresh,
  syncStatus,
  retrySync,
  pendingReviewCount,
  onReviewStatus,
  onRename,
  onAction,
  onAnalyze,
}: {
  peopleControl?: React.ReactNode;
  recordings: Recording[];
  userId: string;
  loading: boolean;
  error: boolean;
  /** The list is shown but may be stale: a reload or live update failed after it loaded. */
  updateError: boolean;
  refresh: () => void;
  syncStatus: RecordingSyncStatus;
  retrySync: () => Promise<void>;
  pendingReviewCount: number;
  onReviewStatus: (status: StatusPayload) => void;
  onRename: (id: string, name: string | null) => Promise<void>;
  onAction: (action: SessionAction, id: string) => Promise<void>;
  onAnalyze: (id: string) => Promise<void>;
}) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const dates = useActivityDates();
  const { from, to } = dates;
  const { filters, onChange: updateFilters } = useSessionFilters();
  const actionItems = useActionItems();
  const { archive } = filters;
  const scope = JSON.stringify([userId, from, to, filters]);
  const [selection, setSelection] = useState<{ scope: string; ids: string[] }>({
    scope: "",
    ids: [],
  });
  const [pending, setPending] = useState<{
    action: SessionAction;
    recordings: Recording[];
  } | null>(null);
  const sessionsHeadingRef = useRef<HTMLHeadingElement>(null);
  const [renaming, setRenaming] = useState<Recording | null>(null);
  const [actionBusy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [analyzeError, setAnalyzeError] = useState<string | null>(null);
  const [startingIds, setStartingIds] = useState<string[]>([]);
  const busy = actionBusy || startingIds.length > 0;
  // Reset the stored selection too, so Back cannot revive a selection from an old scope.
  if (selection.scope !== scope) setSelection({ scope, ids: [] });
  const inPeriod = useMemo(
    () =>
      recordings.filter(item => {
        const date = format(parseUtcDate(item.date_started), "yyyy-MM-dd");
        return (!from || date >= from) && (!to || date <= to);
      }),
    [recordings, from, to],
  );
  const scoped = inPeriod.filter(
    item => archive === "all" || item.include_in_processes === (archive !== "archived"),
  );
  const visible = inPeriod.filter(item =>
    matchesSessionFilters(item, filters, t("dashboard.unnamed_session")),
  );
  const selected = visible.filter(
    item => selection.scope === scope && selection.ids.includes(item.id),
  );
  const select = (ids: string[]) => {
    setSelection({ scope, ids });
    setAnalyzeError(null);
  };
  const analyzable = selected.filter(
    item => canAnalyzeSession(item) && !startingIds.includes(item.id),
  );
  const analyzeSession = async (id: string) => {
    setStartingIds(ids => [...ids, id]);
    try {
      await onAnalyze(id);
      refresh();
      actionItems.refresh();
    } finally {
      setStartingIds(ids => ids.filter(current => current !== id));
    }
  };
  const analyzeSelected = async () => {
    setBusy(true);
    const results = await Promise.allSettled(analyzable.map(item => onAnalyze(item.id)));
    const started = new Set(
      analyzable.filter((_, index) => results[index].status === "fulfilled").map(item => item.id),
    );
    const failed = analyzable.length - started.size;
    select(selected.map(item => item.id).filter(id => !started.has(id)));
    if (failed) setAnalyzeError(t("activity.analyze_failed", { count: failed }));
    if (started.size) {
      refresh();
      actionItems.refresh();
    }
    setBusy(false);
  };
  const requestAction = (action: SessionAction, items: Recording[]) => {
    setActionError(null);
    setPending({ action, recordings: items });
  };
  const groups = new Map<string, Recording[]>();
  for (const item of visible) {
    const key = format(parseUtcDate(item.date_started), "yyyy-MM-dd");
    groups.set(key, [...(groups.get(key) ?? []), item]);
  }
  const process = useProcessOverview([userId], from, to);
  usePageAsk({
    sessionKey: "recordings:mine",
    datePreset: dates.preset,
    page: { kind: "team", name: t("activity.my_title") },
    label: t("activity.my_title"),
    filters: { userIds: [userId], ...pageDateFilters(from, to) },
    suggestions: [t("watchlist.suggestion_apps"), t("watchlist.suggestion_rework")],
  });
  const detailSearch = new URLSearchParams(location.search);
  detailSearch.set("sessions", "mine");
  const ready = !process.loading && !process.error;
  const control =
    "h-[38px] rounded-lg border border-outline bg-surface px-3 text-sm focus-visible:outline-info disabled:opacity-50";
  return (
    <div className="dashboard-page @container space-y-6">
      <div className="dashboard-page-header">
        <div className="min-w-0 flex-[1_1_20rem]">
          <h1 className="dashboard-page-title">{t("activity.my_title")}</h1>
          <p className="dashboard-page-description">{t("activity.my_subtitle")}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          {peopleControl}
          <DashboardDateRange {...dates} activityUserIds={[userId]} />
        </div>
      </div>
      <div className="grid gap-3 @[360px]:grid-cols-2 @[1000px]:grid-cols-4">
        <ActivityMetric
          label={t("dashboard.sessions_captured")}
          value={loading || error ? null : scoped.length}
          icon={<Layers3 size={18} />}
          title={t("activity.in_period")}
          tone="text-violet-600 bg-violet-500/10"
        />
        <ActivityMetric
          label={t("activity.captured_time")}
          value={loading || error ? null : formatDuration(activitySeconds(scoped))}
          icon={<Clock3 size={18} />}
          title={t("activity.in_period")}
          tone="text-cyan-600 bg-cyan-500/10"
        />
        <ActivityMetric
          label={t("activity.processes")}
          value={ready ? process.metrics.processes.length : null}
          icon={<Route size={18} />}
          title={t("activity.active_analysis")}
          tone="text-blue-600 bg-blue-500/10"
        />
        <ActivityMetric
          label={t("activity.apps")}
          value={ready ? process.metrics.appCount : null}
          icon={<Grid2X2 size={18} />}
          title={t("activity.active_analysis")}
          tone="text-indigo-500 bg-indigo-500/10"
        />
      </div>
      {process.error && (
        <p role="alert" className="text-sm text-danger">
          {t("dashboard.load_failed")}{" "}
          <button onClick={process.reload} className="underline">
            {t("retry")}
          </button>
        </p>
      )}
      <div className="rounded-xl border border-info/20 bg-info/5 px-5 py-4">
        <p className="text-sm font-medium text-text-mid">{t("activity.personal_hint")}</p>
        <Link
          to="/settings/"
          className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-info hover:underline"
        >
          {t("activity.capture_settings")}
          <ArrowRight size={14} />
        </Link>
      </div>
      <RecordingSyncRow status={syncStatus} onRetry={retrySync} />
      <PendingReviewSection
        pendingReviewCount={pendingReviewCount}
        onStatus={onReviewStatus}
        onQueueEmpty={() => sessionsHeadingRef.current?.focus({ preventScroll: true })}
      />
      {updateError && (
        <p role="alert" className="text-sm text-warning">
          {t("recordings_update_failed")}{" "}
          <button onClick={refresh} className="underline">
            {t("retry")}
          </button>
        </p>
      )}
      <div className="space-y-1">
        <SessionFilterControls
          title={
            <h2
              ref={sessionsHeadingRef}
              tabIndex={-1}
              className="rounded text-lg font-semibold focus:outline-none"
            >
              {t("my_sessions")}
            </h2>
          }
          recordings={inPeriod}
          filters={filters}
          onChange={updateFilters}
        />
        {!loading && !error && visible.length > 0 && (
          <div
            className={`flex min-h-[38px] flex-wrap items-center gap-3 rounded-lg px-4 py-2 text-sm ${selected.length ? "border border-info/20 bg-info/5" : "border border-transparent"}`}
          >
            <label className="flex items-center gap-3 text-text-mid">
              <input
                type="checkbox"
                disabled={busy}
                checked={selected.length === visible.length}
                ref={element => {
                  if (element)
                    element.indeterminate = selected.length > 0 && selected.length < visible.length;
                }}
                onChange={event => select(event.target.checked ? visible.map(item => item.id) : [])}
                className="h-4 w-4 accent-info"
              />
              {t("activity.select_matching")}
            </label>
            {selected.length > 0 && (
              <>
                <span role="status" className="font-medium">
                  {t("recordings_selected", { count: selected.length })}
                </span>
                {selected.length === 1 && (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => setRenaming(selected[0])}
                    className={`${control} inline-flex items-center gap-2`}
                  >
                    <Pencil size={14} />
                    {t("rename_recording")}
                  </button>
                )}
                {analyzable.length > 0 && (
                  <button
                    disabled={busy}
                    type="button"
                    onClick={() => void analyzeSelected()}
                    className={`${control} inline-flex items-center gap-2`}
                  >
                    <Play size={14} />
                    {t("activity.analyze_selected")}
                  </button>
                )}
                {selected.some(item => item.include_in_processes) && (
                  <button
                    disabled={busy}
                    type="button"
                    onClick={() =>
                      requestAction(
                        "archive",
                        selected.filter(item => item.include_in_processes),
                      )
                    }
                    className={`${control} inline-flex items-center gap-2`}
                  >
                    <Archive size={14} />
                    {t("archive_recording")}
                  </button>
                )}
                {selected.some(item => !item.include_in_processes) && (
                  <button
                    disabled={busy}
                    type="button"
                    onClick={() =>
                      requestAction(
                        "unarchive",
                        selected.filter(item => !item.include_in_processes),
                      )
                    }
                    className={`${control} inline-flex items-center gap-2`}
                  >
                    <ArchiveRestore size={14} />
                    {t("activity.restore")}
                  </button>
                )}
                <button
                  disabled={busy}
                  type="button"
                  onClick={() => requestAction("delete", selected)}
                  className={`${control} inline-flex items-center gap-2 text-danger`}
                >
                  <Trash2 size={14} />
                  {t("delete_selected_recordings")}
                </button>
                <button
                  disabled={busy}
                  type="button"
                  onClick={() => select([])}
                  className="ml-auto text-xs text-text-dim hover:underline"
                >
                  {t("clear_selection")}
                </button>
                {analyzeError && (
                  <p role="alert" className="w-full text-danger">
                    {analyzeError}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {t("unable_to_load_recordings")}{" "}
          <button onClick={refresh} className="underline">
            {t("retry")}
          </button>
        </p>
      ) : loading ? (
        <p role="status">{t("loading_ellipsis")}</p>
      ) : !visible.length ? (
        <div className="rounded-xl border border-outline bg-surface p-8 text-sm text-text-dim">
          {t("activity.empty_personal")}
        </div>
      ) : (
        [...groups.entries()].map(([date, items]) => (
          <section key={date}>
            <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
              <h3 className="font-semibold">
                {new Date(`${date}T00:00:00`).toLocaleDateString(i18n.resolvedLanguage, {
                  weekday: "long",
                  month: "short",
                  day: "numeric",
                  year:
                    Number(date.slice(0, 4)) === new Date().getFullYear() ? undefined : "numeric",
                })}
              </h3>
              <p className="text-sm text-text-dim">
                {t("activity.day_captured", {
                  time: formatDuration(activitySeconds(items)),
                })}
              </p>
            </div>
            <div className="overflow-hidden rounded-xl border border-outline bg-surface">
              {items.map(item => (
                <MySessionRow
                  key={item.id}
                  recording={item}
                  search={detailSearch.toString()}
                  selected={selected.some(recording => recording.id === item.id)}
                  busy={busy}
                  starting={startingIds.includes(item.id)}
                  onAnalyze={() => analyzeSession(item.id)}
                  onSelect={() =>
                    select(
                      selected.some(recording => recording.id === item.id)
                        ? selected
                            .filter(recording => recording.id !== item.id)
                            .map(recording => recording.id)
                        : [...selected.map(recording => recording.id), item.id],
                    )
                  }
                  onRename={() => setRenaming(item)}
                  onAction={action => requestAction(action, [item])}
                />
              ))}
            </div>
          </section>
        ))
      )}
      {renaming && (
        <SessionRenameDialog
          key={renaming.id}
          recording={renaming}
          onRename={onRename}
          onClose={() => setRenaming(null)}
        />
      )}
      {pending && (
        <RecordingBulkActionDialog
          action={pending.action}
          recordings={pending.recordings.map(
            item => recordings.find(current => current.id === item.id) ?? item,
          )}
          statusById={Object.fromEntries(recordings.map(item => [item.id, item.status]))}
          busy={busy}
          error={actionError}
          onCancel={() => {
            if (!busy) setPending(null);
          }}
          onConfirm={async items => {
            if (busy) return;
            setBusy(true);
            setActionError(null);
            const results = await Promise.allSettled(
              items.map(item => onAction(pending.action, item.id)),
            );
            const failed = items.filter((_, index) => results[index].status === "rejected");
            const succeeded = new Set(
              items
                .filter((_, index) => results[index].status === "fulfilled")
                .map(item => item.id),
            );
            setSelection(current => ({
              ...current,
              ids: current.ids.filter(id => !succeeded.has(id)),
            }));
            if (failed.length) {
              setPending({ action: pending.action, recordings: failed });
              select([
                ...new Set([
                  ...selected.map(item => item.id).filter(id => !succeeded.has(id)),
                  ...failed.map(item => item.id),
                ]),
              ]);
              setActionError(
                t(
                  pending.action === "delete"
                    ? "bulk_recording_delete_failed"
                    : "bulk_recording_update_failed",
                  { count: failed.length },
                ),
              );
            } else setPending(null);
            if (succeeded.size) {
              process.reload();
              actionItems.refresh();
            }
            setBusy(false);
          }}
        />
      )}
    </div>
  );
}
